<template>
  <div class="table-component p-2">
    <artisio-table selectable ref="invoiceTable" :pagination="tableData.pagination" :sort="tableData.ordering"
                   :items="settlements" :fields="tableFields" :checkboxes="true" :extra-data="extraData"
                   :search="tableData.search" :enable-simple-search="false" show-toggle-columns
                   :export-permission-name="'export_settlement'" :index="false" :busy="loading"
                   export-vuex-module-name="settlements" :export-attributes="exportAttributes"
                   @paramsChanged="getTableData" @update-toggle-columns="updateToggleColumns" requires-min-height
                   @on-column-save="updateToggleColumns" filter-toggle-string="settlements-table"
                   :enable-frontend-export="enableFrontendExport">

      <template v-slot:header-buttons="{selectedItems}">
        <b-dropdown :text="$t('Actions')" v-permission="'change_settlement'" variant="primary">
          <b-dropdown-item @click="onNewSettlementClick">
            {{ $t('Settlement') }}
          </b-dropdown-item>
          <b-dropdown-item @click="onNewManualSettlementClick" v-if="!hideAction(ACTION_CREATE_MANUAL_SETTLEMENT)">
            {{ $t('Manual Settlement') }}
          </b-dropdown-item>
          <b-dropdown-item @click="onNewIntroductorySettlementClick('auction_introductory')"
                           v-if="!hideAction(ACTION_CREATE_AUCTION_INTRODUCTORY_SETTLEMENT)">
            {{ $t('Introductory Settlement') }}
          </b-dropdown-item>
          <b-dropdown-item @click="onNewIntroductorySettlementClick('manual_introductory')"
                           v-if="!hideAction(ACTION_CREATE_MANUAL_INTRODUCTORY_SETTLEMENT)">
            {{ $t('Manual Introductory Settlement') }}
          </b-dropdown-item>
          <b-dropdown-item @click="onGenerateSettlementsClick" v-if="!hideAction(ACTION_BATCH_SETTLEMENT_GENERATE)">
            {{ $t('Generate Settlements') }}
          </b-dropdown-item>
          <b-dropdown-divider></b-dropdown-divider>
          <b-dropdown-item v-if="!hideAction(PREVIEW_ALL)" :disabled="selectedItems.size === 0"
                           @click="onPreviewAllClick(selectedItems)">
            {{ $t('Preview PDF(s)') }}
          </b-dropdown-item>
          <b-dropdown-item v-if="!hideAction(DOWNLOAD_PDF)" :disabled="selectedItems.size === 0"
                           @click="onDownloadPdfClick(selectedItems)">
            {{ $t('Download PDF(s)') }}
          </b-dropdown-item>
          <b-dropdown-item @click="onSettlementsPayClick(selectedItems)" :disabled="selectedItems.size === 0">
            {{ $t('Pay Settlement(s)') }}
          </b-dropdown-item>
          <b-dropdown-item @click="onSettlementsRefundClick(selectedItems)" :disabled="selectedItems.size === 0">
            {{ $t('Refund Settlement(s)') }}
          </b-dropdown-item>
          <b-dropdown-item @click="onGenerateBatchPaymentsClick(selectedItems)"
                           v-if="!hideAction(ACTION_GENERATE_SETTLEMENT_BATCH_PAYMENTS)">
            {{ $t('Generate Batch Payments') }}
          </b-dropdown-item>
          <b-dropdown-item v-if="!hideAction(ACTION_APPROVE_SETTLEMENT)" :disabled="selectedItems.size === 0"
                           @click="onApproveClick(selectedItems, true)">
            {{ $t('Approve Settlement(s)') }}
          </b-dropdown-item>
          <b-dropdown-item v-if="!hideAction(ACTION_APPROVE_SETTLEMENT)" :disabled="selectedItems.size === 0"
                           @click="onApproveClick(selectedItems, false)">
            {{ $t('Undo Settlement(s) Approval') }}
          </b-dropdown-item>
          <b-dropdown-item
            v-if="!hideAction(ACTION_SYNC_WITH_ACCOUNTING_SYSTEM) && isAccountingEnabled && !isManualAccountingSyncDisabled"
            @click="onSyncWithAccountingSystemClick()">
            {{ $t('Sync with Accounting System') }}
          </b-dropdown-item>
          <b-dropdown-divider></b-dropdown-divider>
          <b-dropdown-item
            :disabled="selectedItems.size === 0"
            @click="onGenerateBatchCheckClick(selectedItems)"
          >
            {{ $t('Generate Batch Checks') }}
          </b-dropdown-item>
          <b-dropdown-item
            :disabled="selectedItems.size === 0"
            @click="onGenerateBacsPaymentsClick(selectedItems)"
          >
            {{ $t('Generate BACS Payments') }}
          </b-dropdown-item>
          <b-dropdown-divider></b-dropdown-divider>
          <template v-if="enableAccountingExport && $hasPermission('export_settlement')">
            <b-dropdown-item @click="onExportClick('xlsx')">
              {{ $t('Export Settlements') }}
            </b-dropdown-item>
            <b-dropdown-item @click="onExportClick('xlsx', true)">
              {{ $t('Export Settlement Payments') }}
            </b-dropdown-item>
            <b-dropdown-divider></b-dropdown-divider>
          </template>
          <b-dropdown-item @click="onSettlementsSendNotificationClick(selectedItems)"
                           :disabled="selectedItems.size === 0">
            {{ $t('Send Settlement Notification(s)') }}
          </b-dropdown-item>
          <b-dropdown-item @click="onSettlementsSendIntroductoryNotificationClick(selectedItems)"
                           :disabled="selectedItems.size === 0">
            {{ $t('Send Introductory Settlement Notification(s)') }}
          </b-dropdown-item>
          <b-dropdown-item @click="onSettlementSendPaymentNotificationClick(selectedItems)"
                           :disabled="selectedItems.size === 0">
            {{ $t('Send Payment Notification(s)') }}
          </b-dropdown-item>
          <b-dropdown-item v-if="enableDebitNoteCreation" :disabled="selectedItems.size === 0"
                           @click="onSendDebitNoteEmails(selectedItems)">
            {{ $t('Send Debit Note Email(s)') }}
          </b-dropdown-item>
          <b-dropdown-divider v-if="enableSepa && sale"/>
          <b-dropdown-item v-if="enableSepa && sale" @click="onSepaGeneration(selectedItems, 'pdf')"
                           :disabled="selectedItems.size === 0">
            {{ $t('Preview SEPA PDF') }}
          </b-dropdown-item>
          <b-dropdown-item v-if="enableSepa && sale" @click="onSepaGeneration(selectedItems, 'xml')"
                           :disabled="selectedItems.size === 0">
            {{ $t('Generate SEPA XML') }}
          </b-dropdown-item>
        </b-dropdown>
      </template>
      <template v-slot:search-form-placeholder>
        <search-collapse table-id="settlements-table">
          <settlement-search :exclude-search-fields="excludeSearchFields"/>
        </search-collapse>
      </template>
      <template slot="cell(settlement_no)" slot-scope="{item: settlement}">
        <span v-if="restrictEdit(settlement) || !$hasPermission('change_settlement')">
          {{ settlement.settlement_no }}
        </span>
        <a v-else href="javascript:void(0)"
           @click="onSettlementEditClick(settlement)">
          {{ settlement.settlement_no }}
        </a>
      </template>
      <template slot="cell(type)" slot-scope="{item: settlement}">
        <span v-as-status>{{ settlement.type | removeUnderscores | toUpperCaseEveryWord | translate }}</span>
      </template>
      <template slot="cell(status)" slot-scope="{item: settlement}">
        <span v-if="settlement.status" v-as-status="settlement.status">
          {{ settlement.status | removeUnderscores | toUpperCaseFirst | translate }}
        </span>
        <span v-else v-as-status>{{ $t('N/A') }}</span>
      </template>
      <template slot="cell(sale_no)" slot-scope="{item: settlement}">
        <router-link v-if="settlement.auction && !enableContinuousAuction" v-permission="'view_auction'"
                     :to="{ name: 'sales.view', params: { id: settlement.auction.uuid }}">
          {{ settlement.auction.sale_no }}
        </router-link>
        <span v-else-if="settlement.auction && enableContinuousAuction">
          {{ settlement.auction.sale_no }}
        </span>
      </template>
      <template slot="cell(customer_uuid)" slot-scope="{item: settlement}">
        <template v-if="settlement.customer">
          <router-link v-if="$hasPermission('view_customer')"
                       :to="{ name: 'customers.dashboard', params: { id: settlement.customer.uuid }}">
            {{ settlement.customer.display_name }}
          </router-link>
          <span v-else>{{ settlement.customer.display_name }}</span>
        </template>
      </template>
      <template slot="cell(total_vendor_vat)" slot-scope="{ item: settlement }">
        {{ getValue(settlement, 'total_vendor_vat') | asCurrency(currency(settlement)) }}
      </template>
      <template slot="cell(customer_company_name)" slot-scope="{item: settlement}">
        <template v-if="settlement.customer">
          <router-link v-if="$hasPermission('view_customer')"
                       :to="{ name: 'customers.dashboard', params: { id: settlement.customer.uuid }}">
            {{ settlement.customer.company_name }}
          </router-link>
          <span v-else>{{ settlement.customer.company_name }}</span>
        </template>
      </template>
      <template slot="cell(receipts)" slot-scope="{item: settlement}">
        <template v-if="settlement.receipts && settlement.receipts.length > 0">
          <router-link :to="{ name: 'receipt.dashboard', params: { id: settlement.receipts[0].uuid }}">
            {{ settlement.receipts[0].receipt_no }}
          </router-link>
          - <span v-if="settlement.receipts[0].receipt_contract_status"
                  v-as-status="settlement.receipts[0].receipt_contract_status">
          {{ settlement.receipts[0].receipt_contract_status | removeUnderscores | toUpperCaseEveryWord }}
        </span>
          <span v-as-status="settlement.receipts[0].receipt_contract_status" v-else>{{ $t('N/A') }}</span>
        </template>
      </template>
      <template slot="cell(contact_person_for_settlement)" slot-scope="{item: settlement}">
        <template v-if="settlement.contact_person_for_settlement">
          <router-link v-if="$hasPermission('view_customer')"
                       :to="{ name: 'customers.dashboard', params: { id: settlement.contact_person_for_settlement.uuid }}">
            {{ settlement.contact_person_for_settlement.display_name }}
          </router-link>
          <span v-else>{{ settlement.contact_person_for_settlement.display_name }}</span>
        </template>
      </template>
      <template slot="cell(amounts)" slot-scope="{item: settlement}">
        {{ getValue(settlement, 'total_amount') | asCurrency(currency(settlement)) }} /
        {{ getValue(settlement, 'paid_amount') | asCurrency(currency(settlement)) }} /
        {{ settlement.outstanding_amount | asCurrency(currency(settlement)) }}
      </template>
      <template slot="cell(total_items)" slot-scope="{item: settlement}">
        {{ getDataObject(settlement).total_financial_items }}
      </template>
      <template slot="cell(last_synced_at)" slot-scope="{item: invoice}">
        {{ invoice.last_synced_at | toDateTime }}
      </template>
      <template slot="cell(created_data)" slot-scope="{item: settlement}">
        {{ settlement.created_at | toDate }} by {{ settlement.created_by.display_name }}
      </template>
      <template slot="cell(updated_data)" slot-scope="{item: settlement}">
        {{ settlement.updated_at | toDate }} by {{ settlement.updated_by.display_name }}
      </template>
      <template slot="cell(notification_sent_at)" slot-scope="{item: settlement}">
        <template v-if="settlement.notification_sent_at">
          <span class="badge badge-success">
            {{ $t('Yes') }}
          </span> -
          {{ settlement.notification_sent_at | toDateTime }}
        </template>
        <template v-else>
           <span class="badge badge-danger">
            {{ $t('No') }}
          </span>
        </template>
      </template>
      <template slot="cell(actions)" slot-scope="{item: settlement}">

        <div class="position-relative">
          <b-dropdown right class="text-white" variant="primary" no-caret size="sm">
            <template #button-content>
              <font-awesome-icon icon="ellipsis-v" class="icon"/>
            </template>

            <b-dd-item @click="onSettlementEditClick(settlement)" v-permission="'change_settlement'"
                       :disabled="restrictEdit(settlement)">
              <font-awesome-icon icon="pen" style="font-size: .9em" class="mr-1"/>
              {{ $t('Edit Settlement') }}
            </b-dd-item>

            <b-dd-item v-permission="'view_settlement'" @click="onPdfViewClick(settlement)">
              <font-awesome-icon icon="eye" style="font-size: .9em" class="mr-1"/>
              {{ $t('View Settlement') }}
            </b-dd-item>

            <b-dd-item @click="onPaymentsClick(settlement)" :disabled="settlement.settlement_payments.length === 0">
              <font-awesome-icon icon="wallet" style="font-size: .9em" class="mr-1"/>
              {{ $t('Payments') }}
            </b-dd-item>

            <b-dd-item @click="onSettlementCancelClick(settlement)"
                       :disabled="restrictEdit(settlement)"
                       v-permission="'change_settlement'" v-if="enableSettlementCancellation">
              <font-awesome-icon icon="ban" style="font-size: .9em" class="mr-1"/>
              {{ $t('Cancel Settlement') }}
            </b-dd-item>
            <template v-for="input in showSettlementExtraOptions" v-if="displaySettlementExtraOptions(settlement)">
              <b-dd-item v-permission="'change_settlement'"
                         v-if="input.show_in_actions && input.does_not_change_amount"
                         :disabled="restrictEdit(settlement)"
                         @click="onSettlementToggleExtraInfo(settlement, input)"
                         :key="`settlement-extra-info-checkboxes-${input.key}`">
                <font-awesome-icon icon="toggle-on" style="font-size: .9em" class="mr-1"
                                   v-if="settlement.extra_info && settlement.extra_info[input.key]"/>
                <font-awesome-icon v-else icon="toggle-off" style="font-size: .9em" class="mr-1"/>
                {{ input.label }}
              </b-dd-item>
            </template>
            <b-dd-item v-if="enableDebitNoteCreation" v-permission="'change_settlement'"
                       :disabled="restrictEdit(settlement) || settlement.type === 'debit_note'"
                       @click="onDebitNoteCreateClick(settlement)">
              <font-awesome-icon icon="file" style="font-size: .9em" class="mr-1"/>
              {{ $t('Create Debit Note') }}
            </b-dd-item>

            <table-notes-button :item="settlement"
                                :source="SOURCE_SETTLEMENTS_TABLE" object-name="settlement" is-dd-item
                                plural-object-name="settlements" change-permission="change_settlement"
                                delete-permission="change_settlement" :total-notes="settlement.total_notes"
                                :hide-footer="!$hasPermission('change_settlement')"/>
          </b-dropdown>
          <b-badge v-if="settlement && settlement.total_notes" class="total-notes">
            {{ settlement.total_notes }}
          </b-badge>
        </div>

      </template>
      <template slot="cell(transaction_history)" slot-scope="{item: settlement}">
        <exports :item="settlement" current-field="settlement_tr_history"
                 :on-show-more-click="onShowTransactionalMoreClick"/>
      </template>
      <template slot="cell(payment_history)" slot-scope="{item: settlement}">
        <exports :item="settlement" current-field="settlement_payments" :on-show-more-click="onShowPaymentMoreClick"/>
      </template>
      <template slot="cell(identity_verification)" slot-scope="{item: settlement}">
        {{ settlement.customer.term ? settlement.customer.term.identity_verification : '' | toUpperCaseFirst }}
      </template>

      <template slot="cell(kyc_status_verification)" slot-scope="{item: settlement}">
        {{ settlement.customer.term ? settlement.customer.term.kyc_status_verification : '' | toUpperCaseFirst }}
      </template>

      <template slot="cell(approved)" slot-scope="{item: settlement}">
        <span v-if="settlement.approved" class="badge-success badge">{{ $t("Yes") }}</span>
        <span v-else class="badge-danger badge">{{ $t("No") }}</span>
      </template>

      <template slot="cell(vendor_payment_method)" slot-scope="{item: settlement}">
        <span v-as-status>{{ settlement.customer.term.payment_method | asPaymentMethod }}</span>
      </template>

      <template slot="cell(total_amount)" slot-scope="{item: settlement}">
        {{ getValue(settlement, 'total_amount') | asCurrency(currency(settlement)) }}
      </template>
      <template slot="cell(paid_amount)" slot-scope="{item: settlement}">
        {{ getValue(settlement, 'paid_amount') | asCurrency(currency(settlement)) }}
      </template>
      <template slot="cell(outstanding_amount)" slot-scope="{item: settlement}">
        {{ settlement.outstanding_amount | asCurrency(currency(settlement)) }}
      </template>

      <template slot="cell(debited_settlement)" slot-scope="{item: settlement}">
        <a v-if="settlement.debited_settlement" href="javascript:void(0)"
           @click="onPdfViewClick(settlement.debited_settlement)">
          {{ settlement.debited_settlement.settlement_no }}
        </a>
      </template>

      <template slot="cell(debit_note)" slot-scope="{item: settlement}">
        <a v-if="settlement.debit_note" href="javascript:void(0)"
           @click="onPdfViewClick(settlement.debit_note, true)">
          {{ settlement.debit_note.settlement_no }}
        </a>
      </template>


      <template slot="cell(vendor_group_account)" slot-scope="{item: settlement}"
                v-if="vendorBuyerGroupAccountsEnabled">
        <template v-if="settlement.auction">
          {{ settlement.auction.vendor_group_account }}
        </template>
      </template>

       <template :slot="`cell(${dynamicField})`" slot-scope="{item, field}"
                v-for="dynamicField in toggleColumns.dynamicFields">
        <span v-if="item.customer.identification && item.customer.identification.dynamic_fields && item.customer.identification.dynamic_fields[dynamicField]"
          v-html="$options.filters.asDynamicField(item.customer.identification.dynamic_fields[dynamicField], field.type, field.choices)">
        </span>
      </template>

      <template slot="cell(billing_address_1)" slot-scope="{item: settlement}">
        <div v-if="settlement.settlement_billing_address">
          {{ settlement.settlement_billing_address.address_1 }}
        </div>
      </template>
      <template slot="cell(billing_address_2)" slot-scope="{item: settlement}">
        <div v-if="settlement.settlement_billing_address">
          {{ settlement.settlement_billing_address.address_2 }}
        </div>
      </template>
      <template slot="cell(billing_state)" slot-scope="{item: settlement}">
        <div v-if="settlement.settlement_billing_address">
          {{ settlement.settlement_billing_address.state }}
        </div>
      </template>
      <template slot="cell(billing_county)" slot-scope="{item: settlement}">
        <div v-if="settlement.settlement_billing_address">
          {{ settlement.settlement_billing_address.county }}
        </div>
      </template>
      <template slot="cell(billing_city)" slot-scope="{item: settlement}">
        <div v-if="settlement.settlement_billing_address">
          {{ settlement.settlement_billing_address.city }}
        </div>
      </template>
      <template slot="cell(billing_zip_code)" slot-scope="{item: settlement}">
        <div v-if="settlement.settlement_billing_address">
          {{ settlement.settlement_billing_address.zip_code }}
        </div>
      </template>
      <template slot="cell(billing_country)" slot-scope="{item: settlement}">
        <div v-if="settlement.settlement_billing_address && settlement.settlement_billing_address.country">
          {{ settlement.settlement_billing_address.country.name }}
        </div>
      </template>
      <template slot="cell(billing_first_name)" slot-scope="{item: settlement}">
        <div v-if="settlement.settlement_billing_address">
          {{ settlement.settlement_billing_address.first_name }}
        </div>
      </template>
      <template slot="cell(billing_last_name)" slot-scope="{item: settlement}">
        <div v-if="settlement.settlement_billing_address">
          {{ settlement.settlement_billing_address.last_name }}
        </div>
      </template>
      <template slot="cell(branch)" slot-scope="{item: settlement}">
        <div v-if="settlement.branch">
          {{ settlement.branch.name }}
        </div>
      </template>

      <template slot="cell(client_tier)" slot-scope="{item: settlement}">
        <client-tier-display-widget :customer="settlement.customer" v-if="settlement.customer"/>
      </template>


      <template slot="cell(date)" slot-scope="{item: settlement}">
        {{ settlement.date | toDate }}
      </template>

    </artisio-table>
    <generate-batch-check-modal/>
    <generate-bacs-payments-modal/>
    <payment-list-modal/>
    <date-range-modal :submit-handler="onDateRangeSubmit" :title="$t(`Settlement Date Range`)" ref="date-range-modal"/>
    <debit-note-modal/>
  </div>
</template>

<script>
  import {createNamespacedHelpers} from "vuex";
  import i18n from "@/shared/i18n";
  import ArtisioTable from "@/core/components/ams-table/ArtisioTable";
  import {
    ACTION_APPROVE_SETTLEMENT,
    ACTION_CREATE_AUCTION_INTRODUCTORY_SETTLEMENT,
    ACTION_CREATE_MANUAL_INTRODUCTORY_SETTLEMENT,
    ACTION_GENERATE_SETTLEMENT_BATCH_PAYMENTS,
    SETTLEMENTS_TABLE_NAME,
    SOURCE_SETTLEMENTS_TABLE
  } from "@/modules/constants";
  import SettlementService from "@/modules/settlements/SettlementService";
  import PaymentListModal from "./PaymentListModal";
  import {
    ACTION_BATCH_SETTLEMENT_GENERATE,
    ACTION_CREATE_MANUAL_SETTLEMENT,
    ACTION_SYNC_WITH_ACCOUNTING_SYSTEM,
    TYPE_PAYMENT,
    TYPE_REFUND,
    WS_ACCOUNTING_SYNC_PROGRESS,
    WS_BATCH_SETTLEMENT_GENERATION_PROGRESS,
    WS_SETTLEMENTS_MERGED_PDF_PREVIEW_GENERATION_PROGRESS,
    PREVIEW_ALL, DOWNLOAD_PDF, WS_SEPA_GENERATION,
    SOURCE_SETTLEMENT_TRANSACTION_EXPORTS,
    SOURCE_SETTLEMENT_PAYMENT_EXPORTS,
  } from "../constants";
  import SearchCollapse from "../../core/components/search-collapse/SearchCollapse";
  import SettlementSearch from "./SettlementSearch";
  import DateRangeModal from "../../core/components/date-range-picker-modal/DateRangeModal";
  import AccountingService from "../setup/accounting/AccountingService";
  import _ from 'lodash';
  import NotificationPdfPreviewService from "../../core/services/notificationPdfPreview.service";
  import TableNotesButton from "@/modules/notes/TableNotesButton";
  import uuidV4 from 'uuid';
  import SepaService from "../sepa/SepaService";
  import DebitNoteModal from "./DebitNoteModal";
  import Exports from "@/core/components/exports/Exports.vue";
  import ExportsService from "@/core/services/exports.service";
  import GenerateBatchCheckModal from "@/modules/settlements/GenerateBatchCheckModal.vue";
  import GenerateBacsPaymentsModal from "@/modules/settlements/GenerateBacsPaymentsModal.vue";
  import InvoicesService from "@/modules/invoices/InvoicesService";
  import ClientTierDisplayWidget from "@/core/widgets/ClientTierDisplayWidget.vue";

  const {mapState, mapActions} = createNamespacedHelpers('settlements');
  const {mapState: globalMapState, mapActions: globalMapActions} = createNamespacedHelpers('global');


  export default {
    name: "SettlementTable",
    props: {
      hideActions: {
        type: Array,
        default: () => []
      },
      excludeColumns: {
        type: Array,
        default: () => []
      },
      accountingSystemParams: {
        type: Object,
        default: () => {
        }
      },
      excludeSearchFields: {
        type: Array,
        default: () => []
      },
      enableFrontendExport: {
        type: Boolean,
        default: false
      },
    },
    components: {
      ClientTierDisplayWidget,
      GenerateBatchCheckModal,
      GenerateBacsPaymentsModal,
      Exports,
      TableNotesButton,
      SettlementSearch,
      SearchCollapse,
      PaymentListModal,
      ArtisioTable,
      DateRangeModal,
      DebitNoteModal,
    },
    data() {
      return {
        ACTION_CREATE_MANUAL_SETTLEMENT: ACTION_CREATE_MANUAL_SETTLEMENT,
        ACTION_CREATE_AUCTION_INTRODUCTORY_SETTLEMENT: ACTION_CREATE_AUCTION_INTRODUCTORY_SETTLEMENT,
        ACTION_CREATE_MANUAL_INTRODUCTORY_SETTLEMENT: ACTION_CREATE_MANUAL_INTRODUCTORY_SETTLEMENT,
        ACTION_GENERATE_SETTLEMENT_BATCH_PAYMENTS: ACTION_GENERATE_SETTLEMENT_BATCH_PAYMENTS,
        ACTION_SYNC_WITH_ACCOUNTING_SYSTEM: ACTION_SYNC_WITH_ACCOUNTING_SYSTEM,
        ACTION_BATCH_SETTLEMENT_GENERATE: ACTION_BATCH_SETTLEMENT_GENERATE,
        SOURCE_SETTLEMENTS_TABLE: SOURCE_SETTLEMENTS_TABLE,
        ACTION_APPROVE_SETTLEMENT: ACTION_APPROVE_SETTLEMENT,
        PREVIEW_ALL: PREVIEW_ALL,
        DOWNLOAD_PDF: DOWNLOAD_PDF,
        exportAttributes: {
          settlement_no: i18n.t('No'),
          type: i18n.t('Type'),
          'auction.sale_no': i18n.t('Sale'),
          'customer.display_name': i18n.t('Vendor'),
          net: i18n.t('Total'),
          paid_amount: i18n.t('Paid'),
          outstanding_amount: i18n.t('Outstanding'),
          total_financial_items: i18n.t('Items'),
          status: i18n.t('Status'),
        },
        processIdToProgressMap: {},
        pdfPreviewRequestId: null,
        batchSettlementGenerationProgress: null,
      }
    },
    computed: {
      ...globalMapState({
        settlementLabels: state => state.settlementLabels,
        enableContinuousAuction: state => state.enableContinuousAuction,
        isAccountingEnabled: state => state.isAccountingEnabled,
        isManualAccountingSyncDisabled: state => state.isManualAccountingSyncDisabled,
        currentUser: state => state.currentUser,
        enableSepa: state => state.enableSepa,
        enableSettlementCancellation: state => state.enableSettlementCancellation,
        enableDebitNoteCreation: state => state.enableDebitNoteCreation,
        enableAccountingExport: state => state.enableAccountingExport,
        showSettlementExtraOptions: state => state.showSettlementExtraOptions,
        vendorBuyerGroupAccountsEnabled: state => state.vendorBuyerGroupAccountsEnabled,
      }),
      ...mapState({
        tableData: state => state.tableData[state.tableSource],
        loading: state => state.tableData[state.tableSource].loading,
        settlements: state => state.tableData[state.tableSource].rows,
        sale: state => state.tableData[state.tableSource].sale,
        toggleColumns: state => state.tableData[state.tableSource].toggleColumns,
        tableSource: state => state.tableSource
      }),
      computedExcludeColumns() {
        const excludeColumns = _.cloneDeep(this.excludeColumns);
        if (this.hideAction(ACTION_SYNC_WITH_ACCOUNTING_SYSTEM) || !this.isAccountingEnabled) {
          excludeColumns.push('last_synced_at');
        }
        if (!this.vendorBuyerGroupAccountsEnabled) {
          excludeColumns.push('vendor_group_account');
        }
        return excludeColumns;
      },
      tableFields() {
        return _.cloneDeep(this.toggleColumns.fields)
          .filter(field => !this.computedExcludeColumns.includes(field.key) && field.isVisible)
      },
      extraData() {
        return {
          ...this.toggleColumns,
          excludeColumns: this.computedExcludeColumns,
          tableName: `${SETTLEMENTS_TABLE_NAME}.${this.tableSource}`,
        }
      },
    },
    methods: {
      ...mapActions([
        'showGenerateBatchCheckModal',
        'showGenerateBacsPaymentsModal',
        'showModal',
        'cancelSettlement',
        'showPaymentModal',
        'showPaymentListModal',
        'showDebitNoteModal',
        'hideDebitNoteModal',
        'getDebitNoteSettlementModalObject',
        'getTableData',
        'getModalData',
        'showManualSettlementModal',
        'getManualSettlementModalData',
        'updateToggleColumns',
        'showIntroductorySettlementModal',
        'getIntroductorySettlementModalData',
        'showTransactionExportsModal',
        'showPaymentExportsModal',
        'showSettlementBatchPaymentModal'
      ]),
      ...globalMapActions(['showDateRangeModal', 'addProgress', 'removeProgress', 'updateProgressValue']),
      restrictEdit(settlement) {
        return settlement.status === 'cancelled' || settlement.status === 'debited'
      },
      displaySettlementExtraOptions(settlement) {
        return ['auction', 'manual'].indexOf(settlement.type) >= 0;
      },
      async onPdfViewClick(settlement) {
        await SettlementService.viewSettlementPdf(this, settlement)
      },
      onNewSettlementClick() {
        this.showModal({
          source: SOURCE_SETTLEMENTS_TABLE,
          customer: this.tableData.customer,
          disableCustomerInput: !!this.tableData.customer,
          sale: this.tableData.sale
        })
      },
      onGenerateBatchCheckClick(items) {
        let filteredItems = new Set([...items].filter(item => item.payable_amount > 0));
        if (filteredItems.size === 0) {
          this.$toast('danger', this.$i18n.t('You need to select at least one settlement with payable amount more than 0'));
          return
        }
        this.showGenerateBatchCheckModal({
          source: SOURCE_SETTLEMENTS_TABLE,
          selectedElements: filteredItems
        })
      },
      onGenerateBacsPaymentsClick(items) {
        let filteredItems = new Set([...items].filter(item => item.payable_amount > 0));
        if (filteredItems.size === 0) {
          this.$toast('danger', this.$i18n.t('You need to select at least one settlement with payable amount more than 0'));
          return
        }
        this.showGenerateBacsPaymentsModal({
          source: SOURCE_SETTLEMENTS_TABLE,
          selectedElements: filteredItems
        })
      },
      onNewManualSettlementClick() {
        this.showManualSettlementModal({source: SOURCE_SETTLEMENTS_TABLE, customer: this.tableData.customer})
      },
      onNewIntroductorySettlementClick(type) {
        this.showIntroductorySettlementModal({
          source: SOURCE_SETTLEMENTS_TABLE,
          customer: this.tableData.customer,
          sale: type === 'auction_introductory' ? this.sale : null,
          type: type
        })
      },
      async onSettlementEditClick(settlement) {
        let params = {
          customer_uuid: settlement.customer_uuid,
        };

        if (settlement.type === 'auction') {
          params.auction_uuid = settlement.auction_uuid;
          this.showModal({source: SOURCE_SETTLEMENTS_TABLE})

          await this.getModalData({
            params,
            object: settlement,
            sale: settlement.auction,
            customer: settlement.customer
          });
        } else if (settlement.type === 'manual') {
          params.is_manual = true;

          this.showManualSettlementModal({source: SOURCE_SETTLEMENTS_TABLE})
          await this.getManualSettlementModalData({
            params,
            object: settlement,
            customer: settlement.customer
          });
        } else if (settlement.type === 'auction_introductory' || settlement.type === 'manual_introductory') {
          this.showIntroductorySettlementModal(
            {
              source: SOURCE_SETTLEMENTS_TABLE,
              customer: this.tableData.customer,
              sale: _.cloneDeep(this.sale),
              type: settlement.type
            }
          )
          this.getIntroductorySettlementModalData({object: settlement})
        } else if (settlement.type === 'debit_note') {
          this.showDebitNoteModal({
            source: SOURCE_SETTLEMENTS_TABLE,
            object: settlement,
          });
          this.getDebitNoteSettlementModalObject({settlement});
        }
      },
      onSettlementCancelClick(settlement) {
        if (settlement.is_legacy) {
          this.$toast('danger', this.$i18n.t('You cannot cancel legacy settlement'));
          return;
        }
        this.$confirm(
          this.$i18n.t('Are you sure you want to cancel following settlement?'),
          this.$i18n.t('Settlement has been successfully canceled'),
          this.cancelSettlement,
          settlement.uuid
        )
      },
      onDebitNoteCreateClick(settlement) {
        if (settlement.is_legacy) {
          this.$toast('danger', this.$i18n.t('You can not create debit note from legacy settlement'));
          return false
        }
        this.showDebitNoteModal({object: settlement, source: 'settlement'});
        this.getDebitNoteSettlementModalObject({settlement: settlement});
      },
      getValue(settlement, attributeName) {
        if (settlement.status === 'cancelled') {
          return settlement.data[attributeName];
        }
        return settlement[attributeName];
      },
      async onSettlementsPayClick(items) {
        const tmpSettlement = items.values().next().value;
        let customerUuid = tmpSettlement.customer_uuid;
        let currencyCode = tmpSettlement.currency.code;

        for (let item of items) {
          if (item.status === 'cancelled') {
            this.$errorDialog(this.$i18n.t('Selected settlements(s) are already cancelled. You can not pay for cancelled settlements.'));
            return;
          }
          if (item.status === 'debited') {
            this.$errorDialog(this.$i18n.t('Selected settlements(s) are already debited. You can not pay for debited settlements.'));
            return;
          }
          if (item.customer_uuid !== customerUuid) {
            this.$errorDialog(this.$i18n.t("You can not process one payment for different customers"));
            return;
          }
          if (item.currency.code !== currencyCode) {
            this.$errorDialog(this.$i18n.t("You can not process one payment for different currencies"));
            return;
          }
        }

        this.showPaymentModal({
          source: SOURCE_SETTLEMENTS_TABLE,
          settlements: [...items],
          type: TYPE_PAYMENT
        });
      },
      async onSettlementsRefundClick(items) {
        const tmpSettlement = items.values().next().value;
        let customerUuid = tmpSettlement.customer_uuid;
        let currencyCode = tmpSettlement.currency.code;

        for (let item of items) {
          if (item.status === 'cancelled') {
            this.$errorDialog(this.$i18n.t('Selected settlements(s) are already cancelled. You can not refund cancelled settlements.'));
            return;
          }
          if (item.status === 'debited') {
            this.$errorDialog(this.$i18n.t('Selected settlements(s) are already debited. You can not refund debited settlements.'));
            return;
          }
          if (item.customer_uuid !== customerUuid) {
            this.$errorDialog(this.$i18n.t("You can not process one payment for different customers"));
            return;
          }
          if (item.currency.code !== currencyCode) {
            this.$errorDialog(this.$i18n.t("You can not process one payment for different currencies"));
            return;
          }
        }

        this.showPaymentModal({
          source: SOURCE_SETTLEMENTS_TABLE,
          settlements: [...items],
          type: TYPE_REFUND
        });
      },
      async onSettlementsSendNotificationClick(items) {
        let uuids = [];
        items.forEach(s => uuids.push(s.uuid));
        const pdfLoadData = {settlement_uuids: uuids},
          notificationData = {uuids: uuids};
        const successMessage = this.$i18n.t('Sending settlement notification(s) successfully started.');
        let handlerData = {
          modelClass: 'settlement',
          module: this,
          pdfLoadFn: SettlementService.generateMergedPdfForSettlements,
          pdfLoadData: pdfLoadData,
          notificationFn: SettlementService.sendSettlementNotifications,
          notificationFnParams: notificationData,
          notificationFnSuccessMessage: successMessage
        };
        await this.$confirm(
          this.$i18n.t("Are you sure you want to send {count} notification(s)?", {count: items.size}),
          null,
          NotificationPdfPreviewService.sendEmailNotifications,
          {
            notificationFn: SettlementService.sendSettlementNotifications,
            data: notificationData,
            successMessage: successMessage,
            module: this
          },
          i18n.t('Please Confirm'),
          this.$i18n.t("Send"),
          this.$i18n.t("Cancel"),
          [{
            submitBtnText: this.$i18n.t("Preview and Send"),
            handler: NotificationPdfPreviewService.previewPdfs,
            handlerParams: handlerData,
            indicateRefusal: true
          }]
        );
      },
      async onSettlementsSendIntroductoryNotificationClick(items) {
        let uuids = [];
        let errMsg = null;
        items.forEach(s => {
          if (s.type === 'auction' || s.type === 'manual') {
            errMsg = this.$i18n.t("Please, select only introductory settlements")
          }
          uuids.push(s.uuid)
        });

        if (errMsg) {
          this.$errorDialog(errMsg)
          return;
        }

        const pdfLoadData = {settlement_uuids: uuids},
          notificationData = {uuids: uuids};
        const successMessage = this.$i18n.t('Sending introductory settlement notification(s) successfully started.');
        let handlerData = {
          modelClass: 'settlement',
          module: this,
          pdfLoadFn: SettlementService.generateMergedPdfForSettlements,
          pdfLoadData: pdfLoadData,
          notificationFn: SettlementService.sendIntroductorySettlementNotifications,
          notificationFnParams: notificationData,
          notificationFnSuccessMessage: successMessage
        };
        await this.$confirm(
          this.$i18n.t("Are you sure you want to send {count} notification(s)?", {count: items.size}),
          null,
          NotificationPdfPreviewService.sendEmailNotifications,
          {
            notificationFn: SettlementService.sendIntroductorySettlementNotifications,
            data: notificationData,
            successMessage: successMessage,
            module: this
          },
          i18n.t('Please Confirm'),
          this.$i18n.t("Send"),
          this.$i18n.t("Cancel"),
          [{
            submitBtnText: this.$i18n.t("Preview and Send"),
            handler: NotificationPdfPreviewService.previewPdfs,
            handlerParams: handlerData,
            indicateRefusal: true
          }]
        );
      },
      async onSendDebitNoteEmails(items) {
        let uuids = [];
        let errMsg = null;
        items.forEach(s => {
          if (s.type !== 'debit_note') {
            errMsg = this.$i18n.t("Please, select only debit notes");
          }
          uuids.push(s.uuid)
        });

        if (errMsg) {
          this.$errorDialog(errMsg)
          return;
        }
        const pdfLoadData = {settlement_uuids: uuids},
          notificationData = {uuids: uuids};
        const successMessage = this.$i18n.t('Sending debit note notification(s) successfully started.');
        let handlerData = {
          modelClass: 'settlement',
          module: this,
          pdfLoadFn: SettlementService.generateMergedPdfForSettlements,
          pdfLoadData: pdfLoadData,
          notificationFn: SettlementService.sendDebitNoteNotification,
          notificationFnParams: notificationData,
          notificationFnSuccessMessage: successMessage
        };

        await this.$confirm(
          this.$i18n.t("Are you sure you want to send {count} notification(s)?", {count: items.size}),
          null,
          NotificationPdfPreviewService.sendEmailNotifications,
          {
            notificationFn: SettlementService.sendDebitNoteNotification,
            data: notificationData,
            successMessage: successMessage,
            module: this
          },
          i18n.t('Please Confirm'),
          this.$i18n.t("Send"),
          this.$i18n.t("Cancel"),
          [{
            submitBtnText: this.$i18n.t("Preview and Send"),
            handler: NotificationPdfPreviewService.previewPdfs,
            handlerParams: handlerData,
            indicateRefusal: true
          }]
        );
      },
      async onSettlementSendPaymentNotificationClick(items) {
        let uuids = [];
        let errMsg = null;
        items.forEach(s => {
          if (s.type === 'debit_note') {
            errMsg = this.$i18n.t("Please, select only manual, auction or introductory settlements")
          } else if (s.status === 'cancelled') {
            errMsg = this.$i18n.t("You can not send payment notification for cancelled settlement(s).")
          }
          uuids.push(s.uuid)
        });

        if (errMsg) {
          this.$errorDialog(errMsg)
          return false;
        }

        let success = await this.$confirm(this.$i18n.t('Are you sure you want to send settlement payment notification(s) for following settlements?'))
        if (!success) {
          return false
        }

        let response = await SettlementService.sendSettlementPaymentNotifications({uuids});

        if (response.success) {
          this.$toast('success', this.$i18n.t('Sending settlement payment notification(s) successfully started.'))
        } else {
          if (response.body.non_field_errors) {
            this.$errorDialog(response.body.non_field_errors[0], this.$i18n.t("Settlement Payment Notification"))
          }
        }
      },
      onPaymentsClick(settlement) {
        this.showPaymentListModal({
          source: SOURCE_SETTLEMENTS_TABLE,
          settlement,
        })
      },
      async onSyncWithAccountingSystemClick() {
        this.showDateRangeModal();
      },
      currency(item) {
        if (item.auction && item.auction.currency) {
          return item.auction.currency.symbol;
        }
        return null;
      },
      hideAction(actionName) {
        return this.hideActions.includes(actionName);
      },
      async onSyncWithAccountingSystemDataReceive(data) {  // TODO refactor to remove code duplication
        for (let process of data) {
          if (process.object_type !== "settlement") {
            continue;
          }
          let progress = this.processIdToProgressMap[process.id];
          if (process.status === 'finished') {
            if (progress) {
              this.removeProgress(progress.uuid);
              delete this.processIdToProgressMap[process.id]
            }
            if (process.errors && Object.keys(process.errors).length > 0) {
              if (this.currentUser.uuid === process.created_by_uuid) {
                this.handleAccountingErrors(process)
              }
            } else {
              this.$toast('success', this.$i18n.t('Synchronization with accounting system successfully finished'))
            }
          } else {
            if (!progress) {
              progress = await this.addProgress(this.$i18n.t("Synchronizing with accounting system, Started by {created_by} at {created_at}", {
                created_by: process.created_by,
                created_at: this.$options.filters.toDateTime(process.created_at),
              }));
            }
            progress = {
              ...progress,
              progressValue: parseInt(Math.round(((process.success_count + process.failed_count) / process.invoice_count) * 100))
            }
            this.updateProgressValue(progress);
            this.processIdToProgressMap[process.id] = progress;
          }
        }
      },
      handleAccountingErrors(process) {
        let errorData = [];
        for (let item of process.errors) {
          Object.keys(item).forEach(key => {
            let errorMessage;
            if (key === "non_field_errors") {
              errorMessage = this.$i18n.t(item[key]);
            } else {
              errorMessage = this.$i18n.t("Settlement: {settlementNo}. Error: {error}", {
                settlementNo: key,
                error: item[key]
              });
            }
            errorData.push(errorMessage);
          })
        }
        const listItemsArr = [];
        errorData.forEach(err => listItemsArr.push(this.$createElement('li', {}, err)))
        this.$errorDialog(this.$createElement('ul', {class: "mb-0"}, listItemsArr), this.$i18n.t("Errors on settlement synchronization"));
      },
      async onDateRangeSubmit(dateRangePickerSelection) {
        let response = await AccountingService.syncWithAccountingSystem({
          ...dateRangePickerSelection,
          ...this.accountingSystemParams,
          'object_type': 'settlement'
        });
        if (response.success) {
          const message = response.body.message ? response.body.message : 'Settlement synchronization with accounting system has successfully started';
          this.$toast('success', this.$i18n.t(message));
          this.$refs["date-range-modal"].onHidden();
        } else if (response.body.non_field_error) {
          this.$errorDialog(response.body.non_field_error[0]);
        }
      },
      onSettlementNotificationPdfsPreviewGenerationProgressReceived(data) {
        NotificationPdfPreviewService.onNotificationPdfsPreviewGenerationProgressReceived(data, this);
      },
      async onGenerateSettlementsClick() {
        let {success, body} = await SettlementService.batchCreateSettlements({'auction_uuid': this.sale.uuid});
        if (success) {
          await this.addBatchSettlementGenerationProgressIfNotExist();
        } else {
          if (body.non_field_errors) {
            this.$errorDialog(body.non_field_errors[0]);
          }
        }
      },
      async onBatchCreateNotificationReceive(data) {
        await this.addBatchSettlementGenerationProgressIfNotExist()
        this.updateProgressValue({
          progressValue: parseInt(Math.round(data.successful / data.total * 100)),
          uuid: this.batchSettlementGenerationProgress.uuid,
        });
        if (data.is_finished) {
          if (!data.errors) {
            this.$toast('success', this.$i18n.t("Batch settlement generation has successfully finished"));
          }
          this.removeProgressIfExist();
          await this.getTableData(null);
        }
        if (data.errors) {
          if (data.errors.non_field_errors) {
            const listItemsArr = [];
            data.errors.non_field_errors.forEach(err => listItemsArr.push(this.$createElement('li', {}, err)))
            this.$errorDialog(this.$createElement('ul', {class: "mb-0"}, listItemsArr), this.$i18n.t("Result"));
          } else {
            this.$errorDialog(this.$i18n.t("Something went wrong while generating batch settlement. Please, try again later."));
          }
        }
      },
      async addBatchSettlementGenerationProgressIfNotExist() {
        if (!this.batchSettlementGenerationProgress) {
          this.batchSettlementGenerationProgress = await this.addProgress(
            this.$i18n.t("Batch settlement generation for current auction")
          );
        }
        return true;
      },
      removeProgressIfExist() {
        if (this.batchSettlementGenerationProgress) {
          this.removeProgress(this.batchSettlementGenerationProgress.uuid);
          this.batchSettlementGenerationProgress = null;
        }
      },
      async onPreviewAllClick(selectedItems) {
        let settlement_uuids = [];
        selectedItems.forEach(item => settlement_uuids.push(item.uuid));
        let response = await NotificationPdfPreviewService.previewPdfs({
          modelType: 'settlement',
          module: this,
          pdfLoadFn: SettlementService.generateMergedPdfForSettlements,
          pdfLoadData: {
            settlement_uuids: settlement_uuids,
          },
        });
        if (!response.success && response.body && response.body.non_field_errors) {
          this.$errorDialog(response.body.non_field_errors[0]);
        }
      },
      async onDownloadPdfClick(selectedItems) {
        let settlement_uuids = [];
        selectedItems.forEach(item => settlement_uuids.push(item.uuid));
        let response = await SettlementService.downloadSettlementPdfs({
          settlement_uuids: settlement_uuids,
        });
        if (response.success) {
          this.$toast('success', this.$i18n.t('PDF download has successfully started'));
        } else if (response.body && response.body.non_field_errors) {
          this.$errorDialog(response.body.non_field_errors[0]);
        } else if (response.body && response.body.settlement_uuids) {
          this.$errorDialog(response.body.settlement_uuids[0]);
        } else if (response.toastError) {
          this.$errorDialog(response.toastError);
        }
      },
      async onSepaGeneration(selectedItems, type) {
        let settlement_uuids = [];
        selectedItems.forEach(item => settlement_uuids.push(item.uuid));
        let response = await SepaService.generateSepa({
          uuids: settlement_uuids,
          type: type,
          auction_uuid: this.sale.uuid
        });
        if (!response.success && response.body && response.body.non_field_errors) {
          this.$errorDialog(response.body.non_field_errors[0]);
        } else {
          this.$toast('success', this.$i18n.t("SEPA Generation started."));
        }
      },
      onSepaGenerationSocket(data) {
        if (data.errors) {
          if (data.errors.non_field_errors) {
            this.$errorDialog(data.errors.non_field_errors)
          } else if (data.errors.errors) {
            const listItemsArr = [];
            data.errors.errors.forEach(err => {
              const errors = [];
              err.errors.forEach(error => errors.push(this.$createElement('li', {}, error)));
              listItemsArr.push(this.$createElement('div', {
                class: 'alert alert-danger',
                role: 'alert'
              }, [this.$createElement('a', {
                attrs: {
                  target: '_blank',
                  href: this.$router.resolve({name: 'customers.dashboard', params: {id: err.uuid}}).href
                }
              }, err.title), this.$createElement('ul', {class: 'mb-0'}, errors)]));
            });
            this.$errorDialog(this.$createElement('div', {class: "mb-0"}, listItemsArr), this.$i18n.t("Sepa Error"));
          } else {
            this.$errorDialog(this.$i18n.t("Something went wrong. Please, try again later"))
          }
          return;
        }

        const id = uuidV4();
        const title = this.$i18n.t(`SEPA Result`);
        let message = this.$i18n.t('SEPA Output is Ready. Click to download.');
        const h = this.$createElement;
        const vNodesMsg = h('p', {class: ['mb-0']}, [message])

        const buttons = [];
        if (data.pdf) {
          buttons.push(h(
            'b-button',
            {
              props: {variant: 'success',},
              on: {
                click: () => {
                  if (!data.xml) {
                    this.$bvToast.hide(id);
                  }
                  window.open(data.pdf, '_blank')
                }
              }
            },
            i18n.t('PDF')
          ));
        }
        if (data.xml) {
          buttons.push(h(
            'b-button',
            {
              class: ['ml-2'],
              props: {variant: 'success',},
              on: {
                click: () => {
                  fetch(data.xml)
                    .then(response => response.text())
                    .then(data => {
                      const xmlBlob = new Blob([data], {type: 'text/xml'});
                      const downloadLink = document.createElement("a");
                      downloadLink.download = `sepa.xml`;
                      downloadLink.href = URL.createObjectURL(xmlBlob);
                      downloadLink.click();
                    });
                }
              }
            },
            i18n.t('XML')
          ));
        }
        buttons.push(h(
          'b-button',
          {
            class: ['ml-2'],
            props: {variant: 'secondary',},
            on: {
              click: () => {
                this.$bvToast.hide(id);
              }
            }
          },
          i18n.t('Dismiss')
        ));

        const buttonsWrapper = h('div', {class: ['mt-2']}, buttons);
        this.$bvToast.toast([vNodesMsg, buttonsWrapper], {
          id: id,
          title: title,
          variant: 'info',
          toaster: 'b-toaster-top-center',
          noAutoHide: true
        })
      },
      getDataObject(settlement) {
        if (settlement.type === 'debit_note' && settlement.debited_settlement) {
          return settlement.debited_settlement.data;
        }
        return settlement;
      },
      async onExportClick(type, isPayment = false) {
        let success = await this.$confirm(
          this.$i18n.t("{count} row(s) will be exported. Do you want to continue?", {count: this.tableData.pagination.total}))
        if (!success) {
          return
        }

        const {
          prepareRequestExportsParams,
          schedulePaymentExportSettlements,
          scheduleExportSettlements
        } = ExportsService

        const requestParams = await prepareRequestExportsParams(this.exportAttributes, "settlements", type)
        const response = isPayment
          ? await schedulePaymentExportSettlements(requestParams)
          : await scheduleExportSettlements(requestParams)

        if (response.success) {
          this.$toast('success', this.$i18n.t('Export generation has successfully started'))
        } else {
          if (response.body.non_field_errors) {
            this.$errorDialog(response.body.non_field_errors[0]);
          } else if (response.toastError) {
            this.$errorDialog(response.toastError);
          }
        }
      },
      onShowTransactionalMoreClick(item) {
        this.showTransactionExportsModal({
          settlement: item,
          source: SOURCE_SETTLEMENT_TRANSACTION_EXPORTS
        })
      },
      onShowPaymentMoreClick(item) {
        this.showPaymentExportsModal({
          settlement: item,
          source: SOURCE_SETTLEMENT_PAYMENT_EXPORTS
        })
      },
      onGenerateBatchPaymentsClick() {
        this.showSettlementBatchPaymentModal({
          source: SOURCE_SETTLEMENTS_TABLE
        })
      },
      async onSettlementToggleExtraInfo(settlement, input) {
        let value = true;
        let confirmText = this.$i18n.t(`Are you sure you want to check "{label}"`, {label: input.label});
        if (settlement && settlement.extra_info && settlement.extra_info[input.key]) {
          confirmText = this.$i18n.t(`Are you sure you want to uncheck "{label}"`, {label: input.label});
          value = false;
        }
        const confirm = await this.$confirm(confirmText)
        if (!confirm) {
          return;
        }

        this.$toast('success', this.$i18n.t('Settlement generation has started'));

        const response = await SettlementService.updateSettlementExtraInfo({uuid: settlement.uuid, [input.key]: value});
        if (response.success) {
          this.$toast('success', this.$i18n.t('Settlement generation has successfully completed.'));
          this.getTableData();
        } else {
          this.$toast('error', response.message)
        }
      },
      async onApproveClick(selectedItems, approve = true) {
        selectedItems = [...selectedItems];
        let settlementNos = []
        let msg = approve ? 'approve' : 'undo settlement approval for'
        for (let item of selectedItems) {
          settlementNos.push(item.settlement_no)
          if (item.status === 'cancelled') {
            this.$toast('danger', this.$i18n.t(`You cannot ${msg} cancelled settlement`));
            return;
          }
          // if (item.status === 'debited') {
          //   this.$toast('danger', this.$i18n.t(`You cannot ${msg} debited settlement`));
          //   return;
          // }
          // if (item.type === 'debit_note') {
          //   this.$toast('danger', this.$i18n.t(`You cannot ${msg} debit note`));
          //   return;
          // }
        }
        const uuids = selectedItems.map(i => {
          return {uuid: i.uuid, approved: approve}
        });

        let success = await this.$confirm(
          approve ?
            this.$i18n.t(`You are trying to approve following settlement(s). Settlements: ${settlementNos.join(', ')}. Do you wish to proceed?`) :
            this.$i18n.t(`You are trying to undo the approval of the following settlement(s). Settlements: ${settlementNos.join(', ')}. Do you wish to proceed?`),
          approve ? this.$i18n.t('Settlement(s) were successfully approved') : this.$i18n.t('Settlement(s) approval was successfully undone'),
          SettlementService.approveSettlements,
          uuids
        );

        if (success) {
          this.getTableData();
          this.$refs.invoiceTable.clearSelected();
        }
      }
    },
    mounted() {
      this.getTableData();
      if (!this.hideAction(ACTION_SYNC_WITH_ACCOUNTING_SYSTEM)) {
        this.$ws.listen(`settlements.${WS_ACCOUNTING_SYNC_PROGRESS}`, this.onSyncWithAccountingSystemDataReceive);
      }
      if (!this.hideAction(ACTION_BATCH_SETTLEMENT_GENERATE)) {
        this.$ws.listen(`settlements.${WS_BATCH_SETTLEMENT_GENERATION_PROGRESS}`, this.onBatchCreateNotificationReceive);
      }
      this.$ws.listen(`settlements.${WS_SEPA_GENERATION}`, this.onSepaGenerationSocket);
      this.$ws.listen(`settlements.${WS_SETTLEMENTS_MERGED_PDF_PREVIEW_GENERATION_PROGRESS}`,
        this.onSettlementNotificationPdfsPreviewGenerationProgressReceived);
    },
    beforeDestroy() {
      this.$ws.remove(`settlements.${WS_ACCOUNTING_SYNC_PROGRESS}`);
      this.$ws.remove(`settlements.${WS_SETTLEMENTS_MERGED_PDF_PREVIEW_GENERATION_PROGRESS}`);
      this.$ws.remove(`settlements.${WS_BATCH_SETTLEMENT_GENERATION_PROGRESS}`);
      this.$ws.remove(`settlements.${WS_SEPA_GENERATION}`);
    }
  }
</script>

<style lang="scss" scoped>
  @import "./scss/variables";

  .total-notes {
    top: -7px;
    right: 12px;
    margin: 0;
    border-radius: 100%;
    padding: 3px 6px;
    font-size: 10px;
    position: absolute;
    background-color: #1ec973;
    color: white;
  }

  .artisio-table {
    /deep/ {
      .customer-uuid-col {
        min-width: 100px;
      }

      .vendor-vat-col {
        min-width: 100px;
      }

      .settlement-no-col {
        min-width: 100px;
      }

      .type-col {
        min-width: 100px;
      }

      .sale-no-col {
        min-width: 100px;
      }

      .amounts-col {
        min-width: 100px;
      }

      .total-items-col {
        min-width: 100px;
      }

      .status-col {
        min-width: 100px;
      }

      .synced-at-col {
        min-width: 100px;
      }

      .actions-col {
        width: 70px;
      }

      .created-data-col {
        min-width: 100px;
      }

      .updated-data-col {
        min-width: 100px;
      }

      .customer_company_name-col {
        min-width: 100px;
      }
    }
  }

  /deep/ .dropdown-menu.show {
    max-height: 280px;
    overflow: auto;
  }
</style>
